import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Droplets, Plus, Check } from 'lucide-react';
import { format } from 'date-fns';

interface PeriodLogDialogProps {
  date: Date;
  onClose: () => void;
  onLog: (flow: 'light' | 'medium' | 'heavy', notes?: string) => void;
  existingLog?: {
    flow: 'light' | 'medium' | 'heavy';
    notes?: string;
  };
  onRemove?: () => void;
}

export function PeriodLogDialog({ date, onClose, onLog, existingLog, onRemove }: PeriodLogDialogProps) {
  const [flow, setFlow] = useState<'light' | 'medium' | 'heavy'>(existingLog?.flow || 'medium');
  const [notes, setNotes] = useState(existingLog?.notes || '');

  const flowOptions = [
    { value: 'light', label: 'Light', icon: <Droplets className="w-4 h-4" /> },
    { value: 'medium', label: 'Medium', icon: <Droplets className="w-4 h-4" /> },
    { value: 'heavy', label: 'Heavy', icon: <Droplets className="w-4 h-4" /> }
  ];

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-xl border border-border max-w-sm w-full overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Log Period</h2>
            <p className="text-sm text-muted-foreground">{format(date, 'MMMM d, yyyy')}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Flow Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Flow</label>
            <div className="grid grid-cols-3 gap-2">
              {flowOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFlow(option.value as 'light' | 'medium' | 'heavy')}
                  className={`p-3 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    flow === option.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  {option.icon}
                  <span className="text-xs">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full h-24 rounded-lg bg-muted p-3 text-sm resize-none"
              placeholder="Add any notes about your flow, symptoms, etc..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-border flex gap-2">
          {existingLog && (
            <button
              onClick={onRemove}
              className="flex-1 px-4 py-2 rounded-lg bg-destructive/20 text-destructive hover:bg-destructive/30 transition-colors text-sm"
            >
              Remove Log
            </button>
          )}
          <button
            onClick={() => onLog(flow, notes)}
            className="flex-1 theme-button px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
          >
            {existingLog ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {existingLog ? 'Update Log' : 'Add Log'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}